import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Provider } from "react-redux";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "next-themes";
import { store } from "@/redux/store";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { ProtectedRoute } from "@/routes/ProtectedRoute";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import Dashboard from "./pages/Dashboard";
import Students from "./pages/Students";
import StudentDetails from "./pages/StudentDetails";
import AcademicYear from "./pages/AcademicYear";
import Subjects from "./pages/Subjects";
import Chapters from "./pages/Chapters";
import Videos from "./pages/Videos";
import Notes from "./pages/Notes";
import ChapterTests from "./pages/ChapterTests";
import WeeklyExams from "./pages/WeeklyExams";
import QuestionBank from "./pages/QuestionBank";
import VideoDetails from "./pages/VideoDetails";
import NoteDetails from "./pages/NoteDetails";
import QuestionManagement from "./pages/QuestionManagement";
import Settings from "./pages/Settings";
import CommunicationSettings from "./pages/CommunicationSettings";
import PlanManagement from "./pages/PlanManagement";
import OrderHistory from "./pages/OrderHistory";
import OrderDetails from "./pages/OrderDetails";
import StudentFeedback from "./pages/StudentFeedback";
import CarouselManagement from "./pages/CarouselManagement";
import Dictionary from "./pages/Dictionary";
import Explore from "./pages/Explore";
import Notifications from "./pages/Notifications";
import NotificationDetails from "./pages/NotificationDetails";
import SendNotification from "./pages/SendNotification";
import ChatGroups from "./pages/ChatGroups";
import ChatGroupDetails from "./pages/ChatGroupDetails";
import NotFound from "./pages/NotFound";
import ExamModule from "./components/ExamModule";

const queryClient = new QueryClient();

const App = () => (
  <Provider store={store}>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/exam-test" element={<ExamModule />} />
              <Route
                element={
                  <ProtectedRoute>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route path="/" element={<Dashboard />} />
                <Route path="/students" element={<Students />} />
                <Route path="/students/:id" element={<StudentDetails />} />
                <Route path="/chat-groups" element={<ChatGroups />} />
                <Route path="/chat-groups/:id" element={<ChatGroupDetails />} />
                <Route path="/years" element={<AcademicYear />} />
                <Route path="/subjects" element={<Subjects />} />
                <Route path="/explore" element={<Explore defaultSection="overview" />} />
                <Route path="/explore/drive" element={<Explore defaultSection="drive" />} />
                <Route path="/explore/dictionary" element={<Explore defaultSection="dictionary" />} />
                <Route path="/explore/dravya" element={<Explore defaultSection="dravya" />} />
                <Route path="/explore/chikitsa" element={<Explore defaultSection="chikitsa" />} />
                <Route path="/explore/submissions" element={<Explore defaultSection="submissions" />} />
                <Route path="/dictionary" element={<Explore defaultSection="dictionary" />} />
                <Route path="/chapters" element={<Chapters />} />
                <Route path="/videos" element={<Videos />} />
                <Route path="/videos/:id" element={<VideoDetails />} />
                <Route path="/notes" element={<Notes />} />
                <Route path="/notes/:id" element={<NoteDetails />} />
                <Route path="/chapter-tests" element={<ChapterTests />} />
                <Route path="/chapter-tests/:testId/questions" element={<QuestionManagement />} />
                <Route path="/weekly-exams" element={<WeeklyExams />} />
                <Route path="/question-bank" element={<QuestionBank />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/plans/management" element={<PlanManagement />} />
                <Route path="/orders" element={<OrderHistory />} />
                <Route path="/orders/:id" element={<OrderDetails />} />
                <Route path="/student-feedback" element={<StudentFeedback />} />
                <Route path="/notifications" element={<Notifications />} />
                <Route path="/notifications/send" element={<SendNotification />} />
                <Route path="/notifications/:id" element={<NotificationDetails />} />
                <Route path="/communication-settings" element={<CommunicationSettings />} />
                <Route path="/carousels" element={<CarouselManagement />} />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </Provider>
);

export default App;
